import React, { useState, useEffect } from 'react';
import { TextField, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { isFieldEdited } from '../../utils/receiptsUtils';

// Helper function to convert date to YYYY-MM-DD format for input
const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  
  // If it's already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Try parsing MM/DD/YYYY or MM/DD/YY format
  const [month, day, year] = dateStr.split('/');
  if (month && day && year) {
    // Handle two-digit years
    const fullYear = year.length === 2 ? '20' + year : year;
    return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  return '';
};

const EditableField = ({ 
  value,
  displayValue,
  onSave,
  label,
  type = 'text',
  isEdited = false,
  disabled = false,
  fullWidth = false,
  className = '',
  floatingEditButton = false, // Position edit button absolutely so it doesn't affect text centering
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(type === 'date' ? formatDateForInput(value) : value);
  const isMobile = useMediaQuery('(max-width:768px)');
  const isEmpty = !value || value.trim() === '';

  useEffect(() => {
    // Update the editValue when the value prop changes
    // This ensures the component re-renders with the latest value from the server
    setEditValue(type === 'date' ? formatDateForInput(value) : value);
  }, [value, type]);

  const handleSave = async () => {
    try {
      await onSave(editValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  const handleCancel = () => {
    setEditValue(type === 'date' ? formatDateForInput(value) : value);
    setIsEditing(false);
  };

  const displayText = !isEditing ? (displayValue || value) : editValue;

  return (
    <div 
      className={`
        ${className}
        ${isEditing ? 'z-10 bg-gray-800 rounded-lg shadow-lg' : ''}
        ${isMobile && isEditing ? 'absolute left-0 right-0 p-3' : 'relative'}
        ${floatingEditButton && !isEditing ? 'inline-block' : ''}
      `}
    >
      {isEditing ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <TextField
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              variant="outlined"
              size="small"
              type={type}
              fullWidth={fullWidth || isMobile}
              autoFocus
              placeholder={`Enter ${label.toLowerCase()}`}
            />
            <IconButton 
              onClick={handleSave} 
              size="small" 
              color="primary" 
              sx={{ 
                padding: isMobile ? '12px' : '4px',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(59, 130, 246, 0.2)',
                }
              }}
            >
              <CheckIcon sx={{ fontSize: isMobile ? '1.5rem' : '1rem' }} />
            </IconButton>
            <IconButton 
              onClick={handleCancel} 
              size="small" 
              color="error" 
              sx={{ 
                padding: isMobile ? '12px' : '4px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                }
              }}
            >
              <CloseIcon sx={{ fontSize: isMobile ? '1.5rem' : '1rem' }} />
            </IconButton>
          </div>
          {type === 'date' && displayValue && (
            <div className="text-xs text-gray-400 italic">
              Current: {displayValue}
            </div>
          )}
        </div>
      ) : (
        <div 
          className={`
            ${floatingEditButton ? 'inline-flex items-center justify-center' : 'flex items-center gap-1'} 
            group min-h-[32px] sm:min-h-[36px] 
            ${!disabled ? 'cursor-pointer' : ''}
          `}
          onClick={() => !disabled && setIsEditing(true)}
        >
          {/* Add invisible spacer on left to balance edit button on right when floating */}
          {floatingEditButton && !disabled && <span className="w-6 sm:hidden" aria-hidden="true" />}
          <span className={`${floatingEditButton ? '' : 'flex-1 min-w-0 truncate'} ${isEdited ? 'text-blue-400' : ''} ${isEmpty ? 'text-gray-500 italic' : ''}`}>
            {isEmpty ? `Add ${label.toLowerCase()}...` : displayText}
          </span>
          {!disabled && (
            <Tooltip title={`Edit ${label}`}>
              <IconButton 
                size="small"
                sx={{ 
                  padding: '4px',
                  flexShrink: 0,
                  opacity: (isMobile || isEmpty) ? 0.7 : 0,
                  transition: 'opacity 0.2s',
                  '.group:hover &': {
                    opacity: 0.7,
                  },
                  '&:hover': {
                    opacity: '1 !important',
                  }
                }}
              >
                <EditIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}
      {/* Add invisible spacer when editing on mobile to prevent content jump */}
      {isMobile && isEditing && (
        <div className="h-16" aria-hidden="true"></div>
      )}
    </div>
  );
};

export default EditableField; 